import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Layers, Cpu, Database, BookOpen, Sparkles, Terminal, ArrowDownRight } from "lucide-react";
import { PROFILE, PROJECTS } from "../data/mock";
import SectionHeader from "../components/common/SectionHeader";
import TechTag from "../components/common/TechTag";
import GithubGrid from "../components/widgets/GithubGrid";
import HeroPortraitFrame from "../components/portfolio/HeroPortraitFrame";
import FeaturedProjectCard from "../components/portfolio/FeaturedProjectCard";
import SupportingProjectCard from "../components/portfolio/SupportingProjectCard";

const CAPABILITIES = [
  {
    icon: Layers,
    title: "Full-stack web systems",
    desc: "Architecting modular, type-safe web applications from token-driven design systems in React/Next.js to resilient backend APIs in Node.js & Python.",
    highlights: ["Next.js (App Router)", "TypeScript", "Tailwind CSS", "REST & FastAPIs"],
    projectRef: "Vistara & MavecodeCourse",
    href: "/works/vistara",
  },
  {
    icon: Cpu,
    title: "Applied AI and retrieval pipelines",
    desc: "Developing production LLM interfaces, vector retrieval workflows, and prompt evaluation systems grounded in measured accuracy rather than raw model hype.",
    highlights: ["Gemini & Claude APIs", "RAG Retrieval", "FastAPI Middleware", "Vector Embeddings"],
    projectRef: "MaveMentor AI",
    href: "/works/mavementor-ai",
  },
  {
    icon: Database,
    title: "Data and systems architecture",
    desc: "Designing persistent data layers, relational & document schemas, authentication workflows, and predictable state transitions for multi-tenant applications.",
    highlights: ["PostgreSQL", "MongoDB", "Auth & Sessions", "Performance Auditing"],
    projectRef: "MavecodeCourse Data Store",
    href: "/works/mavecode-course",
  },
  {
    icon: BookOpen,
    title: "Informatics and technical research",
    desc: "Applying rigorous academic methodologies — systematic literature reviews (SLR), citation graph analysis, and structured evidence synthesis to software domains.",
    highlights: ["SLR Methodology", "Curated Pipelines", "Evaluation Metrics", "Domain Informatics"],
    projectRef: "AI Content Curation Platform",
    href: "/works/ai-content-curation",
  },
];

export default function Home() {
  return (
    <div className="relative">
      {/* 1. HERO SECTION — DISTINCTIVE & CREDIBLE */}
      <section className="relative min-h-[92vh] flex items-center justify-center px-6 sm:px-8 lg:px-12 pt-28 pb-20">
        <div className="hero-glow" />
        <div className="relative z-10 w-full max-w-[1360px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            
            {/* Left Content Column */}
            <div className="lg:col-span-7 text-center lg:text-left">
              {/* Status pill */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2.5 px-3 sm:px-3.5 py-1.5 rounded-full border border-border/80 bg-card/60 backdrop-blur-sm mb-6 max-w-full"
              >
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                </span>
                <span className="font-mono text-[10px] sm:text-[11px] tracking-[0.12em] sm:tracking-[0.18em] uppercase text-foreground/80 font-medium truncate">
                  Full-Stack Systems &middot; Applied AI &middot; Research
                </span>
              </motion.div>

              {/* Prominent Firza Miftahul Ilmi Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.08 }}
                className="text-4xl sm:text-6xl lg:text-[4.75rem] font-medium tracking-display leading-[1.04] text-foreground"
              >
                Firza Miftahul <span className="gradient-purple">Ilmi.</span>
              </motion.h1>

              {/* Concise & Credible Technical Focus */}
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.18 }}
                className="mt-4 sm:mt-5 text-xl sm:text-2xl lg:text-[1.75rem] text-foreground/90 font-medium tracking-tight"
              >
                Engineering deliberate digital systems grounded in academic informatics and production rigor.
              </motion.p>

              {/* Grounded Narrative Subtitle */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.28 }}
                className="mt-5 max-w-[58ch] mx-auto lg:mx-0 text-[15.5px] sm:text-[16.5px] text-muted-foreground leading-relaxed"
              >
                Specializing in modular full-stack web platforms, retrieval-augmented generation (RAG) pipelines, and systematic technical research — with an emphasis on observable architecture and measurable performance.
              </motion.p>

              {/* Action CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.45 }}
                className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3.5 sm:gap-4 justify-center lg:justify-start"
              >
                <a
                  href="#showcase"
                  className="group inline-flex items-center justify-center gap-2.5 h-12 px-6 rounded-full bg-primary text-primary-foreground font-mono text-[11px] sm:text-[11.5px] tracking-[0.16em] sm:tracking-[0.18em] uppercase font-medium hover:bg-primary/90 transition-all shadow-[0_8px_24px_rgba(139,92,246,0.3)] focus-visible:ring-2 focus-visible:ring-primary w-full sm:w-auto"
                >
                  Selected Systems
                  <ArrowDownRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
                </a>
                <Link
                  to="/works"
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-border/80 bg-card/40 hover:bg-secondary/60 hover:border-border transition-colors font-mono text-[11px] sm:text-[11.5px] tracking-[0.16em] sm:tracking-[0.18em] uppercase text-foreground/90 focus-visible:ring-2 focus-visible:ring-primary w-full sm:w-auto"
                >
                  All Case Studies
                </Link>
                <Link
                  to="/articles"
                  className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full border border-transparent hover:border-border/60 hover:bg-card/30 transition-colors font-mono text-[10.5px] sm:text-[11px] tracking-[0.16em] sm:tracking-[0.18em] uppercase text-muted-foreground hover:text-foreground w-full sm:w-auto"
                >
                  Field Notes &rarr;
                </Link>
              </motion.div>
            </div>

            {/* Right Portrait Column — Architectural Waist-Up Frame */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end mt-10 lg:mt-0">
              <HeroPortraitFrame />
            </div>

          </div>
        </div>
      </section>

      {/* 2. CAPABILITIES — 4 COHERENT CATEGORIES */}
      <section className="relative py-24 sm:py-32 px-6 sm:px-8 lg:px-12 border-t border-border/50">
        <div className="max-w-[1360px] mx-auto">
          <SectionHeader
            eyebrow="Core Competencies"
            title="Disciplines &"
            highlight="Capabilities."
            actionHref="/about"
            actionText="Read Bio & Path"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CAPABILITIES.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <motion.div
                  key={cap.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="group rounded-xl border border-border/70 bg-card/45 p-6 hover:border-primary/50 hover:bg-card/75 transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="h-10 w-10 rounded-lg border border-border/80 bg-secondary/50 flex items-center justify-center text-primary mb-5 group-hover:scale-105 transition-transform">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-medium tracking-tight mb-2.5 group-hover:text-primary transition-colors">
                      {cap.title}
                    </h3>
                    <p className="text-[14px] text-foreground/75 leading-relaxed mb-6">
                      {cap.desc}
                    </p>
                  </div>

                  <div>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {cap.highlights.map((h) => (
                        <span
                          key={h}
                          className="font-mono text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 rounded border border-border/60 bg-secondary/30 text-muted-foreground"
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                    <Link
                      to={cap.href}
                      className="pt-3 border-t border-border/40 font-mono text-[10.5px] uppercase tracking-[0.16em] text-primary/90 flex items-center justify-between hover:underline group-hover:text-primary transition-colors"
                    >
                      <span className="flex items-center gap-1.5">
                        <Sparkles className="h-3 w-3" />
                        <span>{cap.projectRef}</span>
                      </span>
                      <span>&rarr;</span>
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. SELECTED WORKS SHOWCASE — MAJOR REDESIGN */}
      <section id="showcase" className="relative py-24 sm:py-32 px-6 sm:px-8 lg:px-12 border-t border-border/50">
        <div className="max-w-[1360px] mx-auto">
          <SectionHeader
            eyebrow="Portfolio Showcase"
            title="Selected"
            highlight="Systems."
            meta={`${String(PROJECTS.length).padStart(2, "0")} engineered platforms`}
            actionHref="/works"
            actionText="Inspect All Work"
          />

          {/* Featured Dominant Project: Vistara */}
          <div className="mb-8 sm:mb-10">
            <FeaturedProjectCard project={PROJECTS[0]} />
          </div>

          {/* Supporting Project Cards: MavecodeCourse, MaveMentor AI, AI Content Curation */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROJECTS.slice(1).map((p, idx) => (
              <SupportingProjectCard key={p.id} project={p} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* 4. OPEN SOURCE & ENGINEERING CADENCE */}
      <section className="relative py-24 sm:py-32 px-6 sm:px-8 lg:px-12 border-t border-border/50">
        <div className="max-w-[1360px] mx-auto">
          <SectionHeader
            eyebrow="Open Engineering"
            title="Building in the"
            highlight="Open."
            meta="@maverick-list"
            actionHref="https://github.com/Maverick-list"
            actionText="Visit GitHub Profile"
          />
          <GithubGrid />
        </div>
      </section>
    </div>
  );
}
